import React from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import Button from "../../componentLibrary/Button";
import Checkbox from "../../componentLibrary/Checkbox";
import { useAuth } from "../../contexts/AuthContext";
import ConfirmationBox from "../../componentLibrary/ConfirmationBox";
import ChangeEmailModal from "../../components/ChangeEmailModal";
import ChangeDisplayNameModal from "../../components/ChangeDisplayNameModal";
import {
  useDeleteAllResponses,
  useDeleteAllRecaps,
  useResetTutorials,
  useDeleteAllFavorites,
  useRequestAccountDeletion,
} from "../../api/mutations";
import {
  useGetUserPreferences,
  useGetResponses,
  useGetRecaps,
  useGetBookmarks,
} from "../../api/queries";
import { useUpdateUserPreferences } from "../../api/mutations";
import { useNotification } from "../../hooks/useNotification";
import { BookmarkTypeEnum } from "../../types";
import { APIError } from "../../utils/ErrorHandling";
import { useErrorHandler } from "../../hooks/useErrorHandler";
import { usePermissions } from "../../contexts/PermissionContext";

const ProfilePage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { notify } = useNotification();
  const { userProfile, loading: permissionsLoading } = usePermissions();

  const { ErrorComponent, handleError } = useErrorHandler({
    messageSource: "api",
    renderText: false,
  });

  const [confirmDeleteLogs, setConfirmDeleteLogs] = React.useState(false);
  const [confirmDeleteInProgressLesson, setConfirmDeleteInProgressLesson] =
    React.useState(false);
  const [confirmResetTutorials, setConfirmResetTutorials] =
    React.useState(false);
  const [confirmDeleteFavorites, setConfirmDeleteFavorites] =
    React.useState(false);
  const [confirmDeleteAccount, setConfirmDeleteAccount] = React.useState(false);
  const [isEmailChangeModalOpen, setIsEmailChangeModalOpen] =
    React.useState(false);
  const [isDisplayNameModalOpen, setIsDisplayNameModalOpen] =
    React.useState(false);

  const [isDeletingLogs, setIsDeletingLogs] = React.useState(false);
  const [isDeletingInProgressLesson, setIsDeletingInProgressLesson] =
    React.useState(false);
  const [isDeletingFavorites, setIsDeletingFavorites] = React.useState(false);
  // Add this after the useGetUserPreferences query
  const { data: pageBookmarks } = useGetBookmarks(BookmarkTypeEnum.PAGE);
  const { data: moduleBookmarks } = useGetBookmarks(BookmarkTypeEnum.MODULE);

  // Calculate if there are any favorites
  const hasFavorites =
    (pageBookmarks?.length || 0) + (moduleBookmarks?.length || 0) > 0;
  // Get user preferences from database
  const {
    data: userPreferences,
    isLoading: preferencesLoading,
    error: preferencesError,
  } = useGetUserPreferences();
  const { data: responses, isLoading: responsesLoading } = useGetResponses();
  const { data: recaps, isLoading: recapsLoading } = useGetRecaps();
  const saveAnswersToLog = userPreferences?.saveAnswersToLog ?? false;
  const askEveryTime = userPreferences?.askEveryTime ?? true;
  const emailAccountIssues = userPreferences?.emailAccountIssues ?? false;
  const emailFeedbackRequests = userPreferences?.emailFeedbackRequests ?? false;
  const emailNewsUpdates = userPreferences?.emailNewsUpdates ?? false;
  const dismissedTutorials = userPreferences?.dismissedTutorials ?? [];
  const hasLogEntries = responses && responses.length > 0;
  const hasInProgressLessons = recaps && recaps.length > 0;
  const isLoadingData = responsesLoading || recapsLoading;

  // Update user preferences mutation
  const updatePreferencesMutation = useUpdateUserPreferences({
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["userPreferences"] });
      notify("success", "Preference saved");
    },
    onError: async () => {
      notify("error", "Failed to save preference");
    },
  });

  const deleteAllLogs = useDeleteAllResponses({
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["responses"] });
      notify("success", "All logs deleted successfully!");
      setIsDeletingLogs(false);
    },
    onError: async () => {
      notify("error", "There was an error deleting your logs.");
      setIsDeletingLogs(false);
    },
  });

  const deleteAllRecaps = useDeleteAllRecaps({
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["recaps"] });
      notify("success", "All sections deleted successfully!");
      setIsDeletingInProgressLesson(false);
    },
    onError: async () => {
      notify("error", "There was an error deleting your sections.");
      setIsDeletingInProgressLesson(false);
    },
  });

  // Reset tutorials mutation
  const resetTutorials = useResetTutorials({
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["userPreferences"] });
      notify(
        "success",
        "Quick Tips reset successfully! They will show again on your next visit.",
      );
      setConfirmResetTutorials(false);
    },
    onError: async (error: APIError) => {
      notify("error", "Failed to reset Quick Tips");
      handleError(error);
      setConfirmResetTutorials(false);
    },
  });

  const deleteAllFavorites = useDeleteAllFavorites({
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["favorites"] });
      await queryClient.invalidateQueries({ queryKey: ["bookmarks"] });
      notify("success", "All favorites deleted successfully!");
      setIsDeletingFavorites(false);
    },
    onError: async () => {
      notify("error", "There was an error deleting your favorites.");
      setIsDeletingFavorites(false);
    },
  });

  const requestAccountDeletion = useRequestAccountDeletion({
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["accountDeletionStatus"],
      });
      notify(
        "success",
        "Account deletion requested. You can recover your account within 30 days.",
      );
      setConfirmDeleteAccount(false);
      handleLogout();
    },
    onError: async () => {
      notify("error", "There was an error requesting account deletion.");
    },
  });

  const { logout: authLogout } = useAuth();

  const handleLogout = async () => {
    // Clear React Query cache to prevent cross-account data leakage
    queryClient.clear();
    await authLogout();
    navigate("/login", { replace: true });
  };

  const handleDeleteLogs = () => {
    setIsDeletingLogs(true);
    setConfirmDeleteLogs(false);
    deleteAllLogs.mutate();
  };

  const handleDeleteInProgressLesson = () => {
    setIsDeletingInProgressLesson(true);
    setConfirmDeleteInProgressLesson(false);
    deleteAllRecaps.mutate();
  };

  const handleDeleteFavorites = () => {
    setIsDeletingFavorites(true);
    setConfirmDeleteFavorites(false);
    deleteAllFavorites.mutate();
  };

  return (
    <div className="flex flex-col mx-auto w-full max-w-sm overflow-hidden px-4 py-6">
      <h1 className="mt-3 mb-1">My Profile</h1>
      <p className="text-lg font-bold mb-1">
        Welcome, {userProfile?.displayName || userProfile?.username}
      </p>
      {/* Display user roles inline if they have any */}
      {!permissionsLoading &&
        userProfile?.roles &&
        userProfile.roles.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-1 mb-4">
            {userProfile.roles.map((role) => (
              <span
                key={role._id}
                className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700 border border-blue-200"
                title={role.description}
              >
                {role.displayName}
              </span>
            ))}
          </div>
        )}
      {(!userProfile?.roles || userProfile.roles.length === 0) &&
        !permissionsLoading && <div className="mb-4" />}

      <hr className="mb-6 border-gray-300" />

      <div className="mb-6">
        <h2 className="text-lg font-semibold mb-4">Account Information</h2>
        <div className="space-y-3">
          <Button
            onClick={() => setIsDisplayNameModalOpen(true)}
            variant="primary"
            className="w-full h-fit flex flex-col items-center"
          >
            <span>Change Display Name</span>
          </Button>
          <Button
            onClick={() => navigate("/confirm-identity")}
            variant="primary"
            className="w-full h-fit"
          >
            Change Password
          </Button>
          <Button
            onClick={() => setIsEmailChangeModalOpen(true)}
            variant="primary"
            className="w-full h-fit"
          >
            Change Email
          </Button>
          <Button
            onClick={() => setConfirmDeleteAccount(true)}
            variant="cautionary"
            className="w-full h-fit"
          >
            Delete Account
          </Button>
        </div>
      </div>

      <hr className="mb-6 border-gray-300" />

      <div className="mb-6">
        <h2 className="text-lg font-semibold mb-4">Preferences</h2>
        <div className="space-y-3">
          {preferencesError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-700 text-sm">
                Error loading preferences: {preferencesError.message}
              </p>
            </div>
          )}
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div>
              <p className="font-medium">Save Answers To Journal</p>
              <p className="text-sm text-gray-600">
                Automatically save your section answers to your journal when
                completing sections
              </p>
              {preferencesLoading && (
                <p className="text-xs text-blue-600 mt-1">
                  Loading preferences...
                </p>
              )}
            </div>
            <Checkbox
              checked={saveAnswersToLog}
              onChange={(e) => {
                updatePreferencesMutation.mutate({
                  saveAnswersToLog: e.target.checked,
                  askEveryTime,
                });
              }}
              label=""
              disabled={preferencesLoading}
            />
          </div>

          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div>
              <p className="font-medium">Ask Every Time</p>
              <p className="text-sm text-gray-600">
                Always show the save preference dialog when completing sections
              </p>
              {preferencesLoading && (
                <p className="text-xs text-blue-600 mt-1">
                  Loading preferences...
                </p>
              )}
            </div>
            <Checkbox
              checked={askEveryTime}
              onChange={(e) => {
                updatePreferencesMutation.mutate({
                  saveAnswersToLog,
                  askEveryTime: e.target.checked,
                });
              }}
              label=""
              disabled={preferencesLoading}
            />
          </div>
        </div>
      </div>

      <hr className="mb-6 border-gray-300" />
      <div className="mb-6">
        <h2 className="text-lg font-semibold mb-4">Email Preferences</h2>
        <div className="space-y-3">
          {preferencesError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-700 text-sm">
                Error loading preferences: {preferencesError.message}
              </p>
            </div>
          )}
          <div className="p-3 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-700 mb-3">
              I agree to receive emails from Psych for Life about:
            </p>
            {preferencesLoading && (
              <p className="text-xs text-blue-600 mb-3">
                Loading preferences...
              </p>
            )}
            <div className="space-y-2 ml-2">
              <div className="flex items-center">
                <Checkbox
                  checked={emailAccountIssues}
                  onChange={(e) => {
                    updatePreferencesMutation.mutate({
                      saveAnswersToLog,
                      askEveryTime,
                      emailAccountIssues: e.target.checked,
                      emailFeedbackRequests,
                      emailNewsUpdates,
                    });
                  }}
                  label=""
                  disabled={preferencesLoading}
                />
                <span className="text-sm text-gray-700 ml-2">
                  Issues with my account (e.g. security issues)
                </span>
              </div>
              <div className="flex items-center">
                <Checkbox
                  checked={emailFeedbackRequests}
                  onChange={(e) => {
                    updatePreferencesMutation.mutate({
                      saveAnswersToLog,
                      askEveryTime,
                      emailAccountIssues,
                      emailFeedbackRequests: e.target.checked,
                      emailNewsUpdates,
                    });
                  }}
                  label=""
                  disabled={preferencesLoading}
                />
                <span className="text-sm text-gray-700 ml-2">
                  Requests for feedback to improve my experience with the app
                </span>
              </div>
              <div className="flex items-center">
                <Checkbox
                  checked={emailNewsUpdates}
                  onChange={(e) => {
                    updatePreferencesMutation.mutate({
                      saveAnswersToLog,
                      askEveryTime,
                      emailAccountIssues,
                      emailFeedbackRequests,
                      emailNewsUpdates: e.target.checked,
                    });
                  }}
                  label=""
                  disabled={preferencesLoading}
                />
                <span className="text-sm text-gray-700 ml-2">
                  News and updates about the app
                </span>
              </div>
            </div>
            <p className="text-xs text-gray-600 italic mt-3">
              We will only contact you when it is necessary or adds real value
              to your experience.
            </p>
          </div>
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-xs text-gray-600">
              Note: Psych for Life® does not sell or provide your data to any
              third-parties.
            </p>
          </div>
        </div>
      </div>

      <hr className="mb-6 border-gray-300" />

      <div className="mb-6">
        <h2 className="text-lg font-semibold mb-4">My Data Settings</h2>
        <div className="space-y-3">
          <Button
            onClick={() => setConfirmResetTutorials(true)}
            variant="secondary"
            className="w-full h-fit"
            disabled={dismissedTutorials.length === 0}
          >
            {dismissedTutorials.length === 0
              ? "All Quick Tips Are Now Visible"
              : "Display All Quick Tips"}
          </Button>
          <Button
            onClick={() => setConfirmDeleteInProgressLesson(true)}
            variant="cautionary"
            className="w-full h-fit"
            disabled={
              isDeletingInProgressLesson ||
              !hasInProgressLessons ||
              isLoadingData
            }
            loading={isDeletingInProgressLesson}
          >
            {!isLoadingData && !hasInProgressLessons
              ? "You Currently Have No In-Progress Sections"
              : "Delete All In-Progress Sections"}
          </Button>

          <div className="mt-4 mb-2">
            <h3 className="text-sm font-medium text-gray-700">
              In All Toolboxes
            </h3>
          </div>

          <Button
            onClick={() => setConfirmDeleteLogs(true)}
            variant="cautionary"
            className="w-full h-fit"
            disabled={isDeletingLogs || !hasLogEntries || isLoadingData}
            loading={isDeletingLogs}
          >
            {!isLoadingData && !hasLogEntries
              ? "You Currently Have No Journal Entries"
              : "Delete All Journal Entries"}
          </Button>
          <Button
            onClick={() => setConfirmDeleteFavorites(true)}
            variant="cautionary"
            className="w-full h-fit"
            disabled={isDeletingFavorites || !hasFavorites}
            loading={isDeletingFavorites}
          >
            {!hasFavorites
              ? "You Currently Have No Pages Favorited"
              : "Unfavorite All of My Pages"}
          </Button>
        </div>
      </div>

      <hr className="mb-6 border-gray-300" />

      <div className="flex justify-center">
        <Button
          onClick={handleLogout}
          variant="secondary"
          roundedFull={true}
          className="h-fit"
        >
          Logout
        </Button>
      </div>

      <ConfirmationBox
        isOpen={confirmDeleteLogs}
        title="Delete Journal Entries?"
        message="Are you sure you want to delete your journal entries, recaps and reflections? This action cannot be undone."
        onConfirm={handleDeleteLogs}
        onCancel={() => setConfirmDeleteLogs(false)}
      />
      <ConfirmationBox
        isOpen={confirmDeleteInProgressLesson}
        title="Delete In-Progress Sections?"
        message="Are you sure you want to delete all of your answers for sections you have in progress?"
        onConfirm={handleDeleteInProgressLesson}
        onCancel={() => setConfirmDeleteInProgressLesson(false)}
      />
      <ConfirmationBox
        isOpen={confirmDeleteFavorites}
        title="Delete All Favorites?"
        message="Are you sure you want to remove all of your favorite pages? This action cannot be undone."
        onConfirm={handleDeleteFavorites}
        onCancel={() => setConfirmDeleteFavorites(false)}
      />
      <ConfirmationBox
        isOpen={confirmResetTutorials}
        title="Display All Quick Tips?"
        message="This will make all dismissed Quick Tips show again. Are you sure you want to display all Quick Tips?"
        onConfirm={() => resetTutorials.mutate({})}
        onCancel={() => setConfirmResetTutorials(false)}
      />
      <ConfirmationBox
        isOpen={confirmDeleteAccount}
        title="Are you sure you want to delete this account?"
        message={
          <div>
            <p className="mb-2">
              You will be able to recover your account within 30 days. If you do
              not recover it within this period, all your data will be
              permanently deleted.
            </p>
            <p className="text-sm text-gray-600">
              This action cannot be undone after the 30-day grace period.
            </p>
          </div>
        }
        confirmText="Delete Account"
        cancelText="Cancel"
        danger={true}
        onConfirm={() => requestAccountDeletion.mutate()}
        onCancel={() => setConfirmDeleteAccount(false)}
      />
      {ErrorComponent}
      <ChangeEmailModal
        isOpen={isEmailChangeModalOpen}
        onClose={() => setIsEmailChangeModalOpen(false)}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ["userPreferences"] });
        }}
      />
      <ChangeDisplayNameModal
        isOpen={isDisplayNameModalOpen}
        onClose={() => setIsDisplayNameModalOpen(false)}
      />
    </div>
  );
};

export default ProfilePage;
